import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, AlertTriangle, Check, X, Loader2, Users } from "lucide-react";

interface ReferralRow {
  id: string;
  referrer_id: string;
  referrer_cashtag: string | null;
  referred_id: string;
  referred_cashtag: string | null;
  status: "pending" | "qualified" | "rejected";
  source: string | null;
  created_at: string;
  qualified_at: string | null;
  referred_signup_ip: string | null;
  referred_device_id: string | null;
  shared_ip_count: number;
  shared_device_count: number;
  referrer_total_referrals: number;
}

type StatusFilter = "all" | "pending" | "qualified" | "rejected";

const formatRelative = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const AdminReferrals = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [filter, setFilter] = useState<StatusFilter>("pending");
  const [rows, setRows] = useState<ReferralRow[] | null>(null);
  const [acting, setActing] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/auth", { replace: true }); return; }
    (async () => {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!data);
    })();
  }, [user, authLoading, navigate]);

  const load = async () => {
    setRows(null);
    const { data, error } = await supabase.rpc("admin_list_referrals", {
      _status: filter === "all" ? null : filter,
      _limit: 100,
    });
    if (error) {
      toast.error(error.message);
      setRows([]);
      return;
    }
    setRows((data ?? []) as ReferralRow[]);
  };

  useEffect(() => {
    if (isAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, filter]);

  const handleAction = async (id: string, status: "qualified" | "rejected") => {
    setActing(id);
    const { error } = await supabase.rpc("admin_set_referral_status", {
      _referral_id: id,
      _status: status,
    });
    setActing(null);
    if (error) { toast.error(error.message); return; }
    toast.success(status === "qualified" ? "Approved" : "Rejected");
    load();
  };

  const stats = useMemo(() => {
    if (!rows) return null;
    const flagged = rows.filter((r) => r.shared_ip_count > 2 || r.shared_device_count > 1).length;
    return { total: rows.length, flagged };
  }, [rows]);

  if (authLoading || isAdmin === null) {
    return (
      <MobileShell>
        <div className="flex-1 grid place-items-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      </MobileShell>
    );
  }

  if (!isAdmin) {
    return (
      <MobileShell>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-4">
          <ShieldCheck className="h-10 w-10 text-muted-foreground" />
          <div>
            <h1 className="font-display text-2xl font-bold">Admins only</h1>
            <p className="text-sm text-muted-foreground mt-1">
              You don't have permission to view referral moderation.
            </p>
          </div>
          <Button variant="secondary" onClick={() => navigate("/wallet")}>Back to wallet</Button>
        </div>
      </MobileShell>
    );
  }

  return (
    <MobileShell>
      <div className="px-5 pt-5 pb-4 border-b border-border">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate("/settings")} className="p-1 -ml-1">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="font-display text-xl font-bold">Referral review</h1>
            <p className="text-[11px] text-muted-foreground">Approve or reject pending referrals</p>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            <Stat icon={<Users className="h-4 w-4" />} label="Showing" value={stats.total} />
            <Stat
              icon={<AlertTriangle className="h-4 w-4 text-destructive" />}
              label="Flagged"
              value={stats.flagged}
              tone={stats.flagged > 0 ? "warn" : undefined}
            />
          </div>
        )}

        <Tabs value={filter} onValueChange={(v) => setFilter(v as StatusFilter)}>
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="qualified">Approved</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
            <TabsTrigger value="all">All</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {rows === null ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-10">No referrals in this view.</p>
        ) : (
          rows.map((r) => {
            const flagged = r.shared_ip_count > 2 || r.shared_device_count > 1;
            return (
              <div key={r.id} className={"rounded-2xl border p-4 bg-card " + (flagged ? "border-destructive/40" : "border-border")}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">
                      ${r.referrer_cashtag ?? "unknown"} <span className="text-muted-foreground font-normal">referred</span> ${r.referred_cashtag ?? "unknown"}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {formatRelative(r.created_at)} · source: {r.source ?? "n/a"}
                    </p>
                  </div>
                  <StatusBadge status={r.status} />
                </div>

                <div className="grid grid-cols-2 gap-2 mt-3 text-[11px]">
                  <Signal label="Shared IPs" value={r.shared_ip_count} bad={r.shared_ip_count > 2} />
                  <Signal label="Shared device" value={r.shared_device_count} bad={r.shared_device_count > 1} />
                  <Signal label="Referrer total" value={r.referrer_total_referrals} bad={r.referrer_total_referrals > 10} />
                  <Signal label="IP" value={r.referred_signup_ip ? r.referred_signup_ip.toString() : "—"} mono />
                </div>

                {flagged && (
                  <div className="mt-3 flex items-center gap-2 text-[11px] text-destructive">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Suspicious pattern — review carefully
                  </div>
                )}

                {r.status !== "qualified" && r.status !== "rejected" && (
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <Button
                      size="sm" variant="secondary"
                      disabled={acting === r.id}
                      onClick={() => handleAction(r.id, "rejected")}
                    >
                      <X className="h-4 w-4" /> Reject
                    </Button>
                    <Button
                      size="sm"
                      disabled={acting === r.id}
                      onClick={() => handleAction(r.id, "qualified")}
                    >
                      {acting === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Approve
                    </Button>
                  </div>
                )}
                {(r.status === "qualified" || r.status === "rejected") && (
                  <div className="mt-3">
                    <Button
                      size="sm" variant="ghost"
                      disabled={acting === r.id}
                      onClick={() => handleAction(r.id, r.status === "qualified" ? "rejected" : "qualified")}
                      className="text-xs"
                    >
                      {acting === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                      Change to {r.status === "qualified" ? "rejected" : "approved"}
                    </Button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </MobileShell>
  );
};

const Stat = ({ icon, label, value, tone }: { icon: React.ReactNode; label: string; value: number; tone?: "warn" }) => (
  <div className={"rounded-xl border p-3 " + (tone === "warn" ? "border-destructive/40 bg-destructive/5" : "border-border bg-card")}>
    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">{icon}{label}</div>
    <p className="text-xl font-display font-bold mt-0.5">{value}</p>
  </div>
);

const StatusBadge = ({ status }: { status: string }) => {
  const map: Record<string, string> = {
    pending: "bg-muted text-muted-foreground",
    qualified: "bg-primary/15 text-primary",
    rejected: "bg-destructive/15 text-destructive",
  };
  return (
    <span className={"text-[10px] uppercase tracking-wide font-semibold px-2 py-1 rounded-full shrink-0 " + (map[status] ?? "bg-muted")}>
      {status}
    </span>
  );
};

const Signal = ({ label, value, bad, mono }: { label: string; value: string | number; bad?: boolean; mono?: boolean }) => (
  <div className={"rounded-lg px-2 py-1.5 " + (bad ? "bg-destructive/10 text-destructive" : "bg-muted/50 text-foreground")}>
    <p className="text-[10px] uppercase tracking-wide opacity-70">{label}</p>
    <p className={"text-xs font-medium truncate " + (mono ? "font-mono" : "")}>{value}</p>
  </div>
);

export default AdminReferrals;
